import { type AsyncResult, promiseToAsyncResult } from "@/cache/AsyncResult";
import { shaderCompiler } from "@/layers/shaders";
import {
  bufferData,
  constantByName,
  createBuffer,
  createFramebuffer,
  createRenderTexture,
} from "@/layers/utility/webgl";
import { ShaderBuilder } from "@/shader-compiler";
import type { GridDimension } from "@mm/api.meteomatics.com";
import type { InterpolationMode } from "@mm/weather-parameter-utils";

const MACRO_BRIGHTNESS_CONTRAST_SATURATION = "USE_BCS";
const MACRO_CATMULL_ROM_SAMPLING = "USE_CATMULL_ROM_SAMPLING";

export interface PrepassFramebuffer {
  dataRasterizationSize: GridDimension;
  framebuffer: WebGLFramebuffer;
  color: WebGLTexture;
}

export function createPrepassFramebuffer(
  gl: WebGL2RenderingContext,
  dataRasterizationSize: GridDimension,
  interpolationMode: InterpolationMode,
): PrepassFramebuffer {
  const framebuffer = createFramebuffer(gl);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

  // rgba
  const color = createRenderTexture(gl, dataRasterizationSize, {
    internalFormat: gl.RGBA,
    format: gl.RGBA,
    type: gl.UNSIGNED_BYTE,
    interpolationMode,
  });
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, color, 0);

  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);

  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    throw Error(`failed to construct framebuffer for prepass. Status ${constantByName(gl, status)}`);
  }

  return {
    dataRasterizationSize,
    framebuffer,
    color,
  };
}

export interface PrepassProgram {
  program: WebGLProgram;
  buffer_QuadVertices: WebGLBuffer;
  buffer_TextureCoordinates: WebGLBuffer;
  a_position: number;
  a_texCoord: number;
  u_mercatorToGl: WebGLUniformLocation;
  u_tileSampler: WebGLUniformLocation;
  u_opacity: WebGLUniformLocation;
}

export function createPrepassProgram(gl: WebGL2RenderingContext): AsyncResult<PrepassProgram> {
  return promiseToAsyncResult(
    shaderCompiler.compileProgram(gl, "prepass_tile.vert", "prepass_tile.frag").then((program) => {
      // TODO: this should be somehow picked up in the GUI
      if (program instanceof Error) {
        throw program;
      }

      const u_mercatorToGl = gl.getUniformLocation(program, "u_mercatorToGl");
      if (u_mercatorToGl == null) {
        throw Error("u_mercatorToGl not found in shader.");
      }
      const u_tileSampler = gl.getUniformLocation(program, "u_tileSampler");
      if (u_tileSampler == null) {
        throw Error("u_tileSampler not found in shader.");
      }
      const u_opacity = gl.getUniformLocation(program, "u_opacity");
      if (u_opacity == null) {
        throw Error("u_opacity not found in shader.");
      }

      const a_position = gl.getAttribLocation(program, "a_position");
      const buffer_QuadVertices = createBuffer(gl);
      bufferData(gl, buffer_QuadVertices);

      const a_texCoord = gl.getAttribLocation(program, "a_texCoord");

      const buffer_TextureCoordinates = createBuffer(gl);
      bufferData(gl, buffer_TextureCoordinates);

      return {
        program,
        buffer_QuadVertices,
        buffer_TextureCoordinates,
        a_position,
        a_texCoord,
        u_mercatorToGl,
        u_tileSampler,
        u_opacity,
      };
    }),
  );
}

export interface MainpassProgram {
  program: WebGLProgram;
  buffer_QuadVertices: WebGLBuffer;
  buffer_TextureCoordinates: WebGLBuffer;
  a_position: number;
  a_texCoord: number;
  u_sampler: WebGLUniformLocation;
  u_samplerPrevious: WebGLUniformLocation;
  u_props: WebGLUniformLocation;
  u_bcs: WebGLUniformLocation;
}

export function createMainpassProgram(gl: WebGL2RenderingContext): AsyncResult<MainpassProgram> {
  const vertexShader = new ShaderBuilder("mainpass.vert"); //are these shaders still valid for WMTS
  const fragmentShader = new ShaderBuilder("mainpass.frag");
  fragmentShader.set(MACRO_BRIGHTNESS_CONTRAST_SATURATION, true);
  fragmentShader.set(MACRO_CATMULL_ROM_SAMPLING, false);

  return promiseToAsyncResult(
    shaderCompiler.compileProgram(gl, vertexShader, fragmentShader).then((program) => {
      // TODO: this should be somehow picked up in the GUI
      if (program instanceof Error) {
        throw program;
      }

      const u_sampler = gl.getUniformLocation(program, "u_sampler");
      if (u_sampler == null) {
        throw Error("u_sampler not found in shader.");
      }
      const u_samplerPrevious = gl.getUniformLocation(program, "u_samplerPrevious");
      if (u_samplerPrevious == null) {
        throw Error("u_samplerPrevious not found in shader.");
      }
      const u_props = gl.getUniformLocation(program, "u_props");
      if (u_props == null) {
        throw Error("u_props not found in shader.");
      }
      const u_bcs = gl.getUniformLocation(program, "u_bcs");
      if (u_bcs == null) {
        throw Error("u_bcs not found in shader.");
      }

      const a_position = gl.getAttribLocation(program, "a_position");
      const buffer_QuadVertices = createBuffer(gl);
      bufferData(gl, buffer_QuadVertices);

      const a_texCoord = gl.getAttribLocation(program, "a_texCoord");

      const buffer_TextureCoordinates = createBuffer(gl);
      bufferData(gl, buffer_TextureCoordinates);

      return {
        program,
        buffer_QuadVertices,
        buffer_TextureCoordinates,
        a_position,
        a_texCoord,
        u_sampler,
        u_samplerPrevious,
        u_props,
        u_bcs,
      };
    }),
  );
}
